/**
 * Chain swapping utilities for flowchart reordering
 * 
 * Handles swapping two adjacent chains when < or > buttons are clicked.
 * Uses tetris-style positioning to avoid overlaps.
 */

import { DEFAULT_NODE_WIDTH, DEFAULT_NODE_HEIGHT, HORIZONTAL_GAP, PADDING } from '../../layout/constants';
import { isMilestoneNode, getMilestoneAnchor, getMilestoneLayoutDims } from '../../utils/milestoneUtils';

/**
 * Build detailed node info array from a chain
 * @param {Object} chain - Chain object with taskIds
 * @param {Object} positions - Current positions map
 * @param {Object} nodeDimensions - Node dimensions map
 * @returns {Array} Array of node info objects
 */
function buildChainNodes(chain, positions, nodeDimensions) {
  const nodes = [];
  chain.taskIds.forEach(taskId => {
    const pos = positions[taskId];
    if (!pos || !Number.isFinite(pos.x) || !Number.isFinite(pos.y)) return;
    const dims = nodeDimensions[taskId] || { width: DEFAULT_NODE_WIDTH, height: DEFAULT_NODE_HEIGHT };
    const width = dims.width || DEFAULT_NODE_WIDTH;
    const height = dims.height || DEFAULT_NODE_HEIGHT;
    nodes.push({
      id: taskId,
      x: pos.x,
      y: pos.y,
      width,
      height,
      right: pos.x + width,
      bottom: pos.y + height,
    });
  });
  return nodes;
}

/**
 * Milestones are excluded from chain taskIds (they don't participate in braces),
 * but they're visually anchored to a host mission. Include them as extra chain
 * nodes so they travel with their host during swaps and count for collision.
 * @param {Object} chain - Chain object with taskIds
 * @param {Array} tasks - All task objects (used to find anchored milestones)
 * @param {Object} positions - Current positions map
 * @param {Object} nodeDimensions - Node dimensions map
 * @returns {Array} Array of node info objects for anchored milestones
 */
function buildAnchoredMilestoneNodes(chain, tasks, positions, nodeDimensions) {
  if (!tasks || tasks.length === 0) return [];
  const chainTaskIds = new Set(chain.taskIds);
  const nodes = [];
  tasks.forEach((task) => {
    if (!isMilestoneNode(task)) return;
    const anchor = getMilestoneAnchor(task);
    if (!anchor || !chainTaskIds.has(anchor.hostTaskId)) return;
    const pos = positions[task.id];
    if (!pos || !Number.isFinite(pos.x) || !Number.isFinite(pos.y)) return;
    const { width, height } = getMilestoneLayoutDims(nodeDimensions, task.id);
    nodes.push({
      id: task.id,
      x: pos.x,
      y: pos.y,
      width,
      height,
      right: pos.x + width,
      bottom: pos.y + height,
    });
  });
  return nodes;
}

/** Chain nodes plus any milestones anchored to hosts inside the chain. */
function buildChainNodesWithMilestones(chain, tasks, positions, nodeDimensions) {
  return [
    ...buildChainNodes(chain, positions, nodeDimensions),
    ...buildAnchoredMilestoneNodes(chain, tasks, positions, nodeDimensions),
  ];
}

/**
 * Get bounding box of a chain's nodes
 * @param {Array} nodes - Array of node info objects
 * @returns {Object|null} { minX, maxX } or null if no nodes
 */
function getChainBounds(nodes) {
  if (nodes.length === 0) return null;
  let minX = Infinity, maxX = -Infinity;
  nodes.forEach(n => {
    minX = Math.min(minX, n.x);
    maxX = Math.max(maxX, n.right);
  });
  return { minX, maxX };
}

/**
 * Sort chains by visual X (effectiveOrder when present, else brace/position.x).
 */
function sortChainsByVisualOrder(chainInfo) {
  return [...chainInfo].sort((a, b) => {
    if (a.effectiveOrder !== undefined && b.effectiveOrder !== undefined) {
      return a.effectiveOrder - b.effectiveOrder;
    }
    return (a.position?.x ?? 0) - (b.position?.x ?? 0);
  });
}

/**
 * Calculate where a chain should be positioned using tetris logic (move LEFT)
 * 
 * For each node in the chain, find vertically overlapping fixed nodes
 * and determine the leftmost valid X position.
 * 
 * @param {Array} chainNodes - Nodes in the chain being positioned
 * @param {Array} fixedNodes - Already-placed nodes that can't move
 * @param {number} gap - Minimum horizontal gap
 * @returns {number} Target minX for the chain
 */
function calculateTetrisPositionLeft(chainNodes, fixedNodes, gap) {
  if (chainNodes.length === 0) return PADDING;
  if (fixedNodes.length === 0) return PADDING;
  
  // Find the chain's current leftmost X
  let chainMinX = Infinity;
  chainNodes.forEach(node => {
    chainMinX = Math.min(chainMinX, node.x);
  });
  
  // Calculate the maximum amount we can shift the chain left
  // Start with the maximum possible shift (all the way to PADDING)
  let maxShiftLeft = chainMinX - PADDING;
  
  // For each node in the chain, check what limits its leftward movement
  chainNodes.forEach(chainNode => {
    // Find all fixed nodes that overlap vertically with this chain node
    const verticallyOverlapping = fixedNodes.filter(fixed => {
      return chainNode.y < fixed.bottom && (chainNode.y + chainNode.height) > fixed.y;
    });
    
    if (verticallyOverlapping.length > 0) {
      // Find the rightmost edge of overlapping fixed nodes
      let rightmostEdge = -Infinity;
      verticallyOverlapping.forEach(fixed => {
        rightmostEdge = Math.max(rightmostEdge, fixed.right);
      });
      
      // This node must be at least gap pixels from the rightmost edge
      const minAllowedX = rightmostEdge + gap;
      
      // How far can this specific node shift left?
      const nodeMaxShiftLeft = chainNode.x - minAllowedX;
      
      // The chain's shift is limited by the most constrained node
      maxShiftLeft = Math.min(maxShiftLeft, nodeMaxShiftLeft);
    }
    // If no vertical overlap, this node doesn't constrain leftward movement
  });
  
  // Don't shift right (negative shift), only left or stay in place
  maxShiftLeft = Math.max(0, maxShiftLeft);
  
  // Return the target minX after applying the shift
  return chainMinX - maxShiftLeft;
}

/**
 * Calculate where a chain should be positioned to the RIGHT of fixed nodes using tetris logic
 * 
 * For each node in the chain, find vertically overlapping fixed nodes
 * and determine the minimum X position that avoids overlap.
 * 
 * When no fixed nodes vertically overlap this chain, keep the current minX.
 * Snapping to PADDING in that case pulls the chain across the board and causes overlaps.
 * 
 * @param {Array} chainNodes - Nodes in the chain being positioned
 * @param {Array} fixedNodes - Already-placed nodes that can't move
 * @param {number} gap - Minimum horizontal gap
 * @returns {number} Target minX for the chain (will be to the right of overlapping fixed nodes)
 */
export function calculateTetrisPositionRight(chainNodes, fixedNodes, gap) {
  if (chainNodes.length === 0) return PADDING;

  let chainMinX = Infinity;
  chainNodes.forEach(node => {
    chainMinX = Math.min(chainMinX, node.x);
  });

  if (fixedNodes.length === 0) return chainMinX;
  
  // For each node in the chain, calculate the minimum X it must be at
  // to avoid overlapping with fixed nodes at the same Y level
  let requiredMinX = -Infinity;
  let hasVerticalConstraint = false;
  
  chainNodes.forEach(chainNode => {
    // Find all fixed nodes that overlap vertically with this chain node
    const verticallyOverlapping = fixedNodes.filter(fixed => {
      return chainNode.y < fixed.bottom && (chainNode.y + chainNode.height) > fixed.y;
    });
    
    if (verticallyOverlapping.length > 0) {
      hasVerticalConstraint = true;
      // Find the rightmost edge of overlapping fixed nodes
      let rightmostEdge = -Infinity;
      verticallyOverlapping.forEach(fixed => {
        rightmostEdge = Math.max(rightmostEdge, fixed.right);
      });
      
      // This node must be at least gap pixels from the rightmost edge
      const minAllowedX = rightmostEdge + gap;
      
      // Calculate what the chain's minX would need to be for this node to be at minAllowedX
      const nodeOffsetFromChainMin = chainNode.x - chainMinX;
      const chainMinXForThisNode = minAllowedX - nodeOffsetFromChainMin;
      
      requiredMinX = Math.max(requiredMinX, chainMinXForThisNode);
    }
  });

  if (!hasVerticalConstraint) {
    return chainMinX;
  }
  
  return Math.max(PADDING, requiredMinX);
}

/**
 * Build the position map used for chain swap.
 * Priority matches on-screen rendering: runtime custom → display (ELK/saved merge) → task.x/y.
 */
export function buildPositionsForChainSwap(tasks, customPositions, displayPositions) {
  const positions = {};
  (tasks || []).forEach((task) => {
    const custom = customPositions?.[task.id];
    if (custom && Number.isFinite(custom.x) && Number.isFinite(custom.y)) {
      positions[task.id] = { x: custom.x, y: custom.y };
      return;
    }
    const display = displayPositions?.[task.id];
    if (display && Number.isFinite(display.x) && Number.isFinite(display.y)) {
      positions[task.id] = { x: display.x, y: display.y };
      return;
    }
    if (Number.isFinite(task.x) && Number.isFinite(task.y)) {
      positions[task.id] = { x: task.x, y: task.y };
    }
  });
  return positions;
}

/**
 * Swap two adjacent chains with proper tetris positioning
 * 
 * Logic:
 * 1. Chain moving LEFT: Tetris against everything to its left (fixed chains)
 * 2. Chain moving RIGHT: Tetris against the chain that just moved left
 * 3. All chains AFTER: Tetris against the swapped chains as a group
 * 
 * @param {Object} positions - Current positions map (taskId -> { x, y })
 * @param {Array} tasks - Array of task objects
 * @param {Object} nodeDimensions - Map of taskId -> { width, height }
 * @param {string} chainId - ID of the chain being moved
 * @param {number} direction - -1 for left, +1 for right
 * @param {Array} chainInfo - Current chain info from layout [{ id, taskIds, position, width }]
 * @returns {Object} New positions map with properly positioned chains
 */
export function swapChainsAndCompact(positions, tasks, nodeDimensions, chainId, direction, chainInfo) {
  if (!positions || !chainInfo || chainInfo.length <= 1) {
    return positions;
  }
  
  // Sort chains by visual order (same basis as arrow enablement / "Chain N" labels)
  const sortedChains = sortChainsByVisualOrder(chainInfo);
  const chainIndex = sortedChains.findIndex(c => c.id === chainId);
  
  if (chainIndex === -1) {
    console.warn('[swapChainsAndCompact] Chain not found:', chainId);
    return positions;
  }
  
  const neighborIndex = chainIndex + direction;
  if (neighborIndex < 0 || neighborIndex >= sortedChains.length) {
    return positions;
  }
  
  const newPositions = { ...positions };
  
  // Determine which chain ends up on the left vs right after swap
  const leftIndex = direction < 0 ? neighborIndex : chainIndex;
  const rightIndex = direction < 0 ? chainIndex : neighborIndex;
  
  const chainMovingLeft = sortedChains[rightIndex]; // Was on right, moving to left
  const chainMovingRight = sortedChains[leftIndex]; // Was on left, moving to right
  
  const nodesMovingLeft = buildChainNodes(chainMovingLeft, positions, nodeDimensions);
  const nodesMovingRight = buildChainNodes(chainMovingRight, positions, nodeDimensions);
  
  if (nodesMovingLeft.length === 0 || nodesMovingRight.length === 0) {
    return positions;
  }

  // Refuse partial swaps — missing nodes leave cards behind and empty braces.
  if (
    nodesMovingLeft.length !== chainMovingLeft.taskIds.length ||
    nodesMovingRight.length !== chainMovingRight.taskIds.length
  ) {
    console.warn('[swapChainsAndCompact] Incomplete positions for chain swap; aborting');
    return positions;
  }

  // Anchored milestones travel with their host chain (and count for collision)
  nodesMovingLeft.push(...buildAnchoredMilestoneNodes(chainMovingLeft, tasks, positions, nodeDimensions));
  nodesMovingRight.push(...buildAnchoredMilestoneNodes(chainMovingRight, tasks, positions, nodeDimensions));
  
  const boundsMovingLeft = getChainBounds(nodesMovingLeft);
  const boundsMovingRight = getChainBounds(nodesMovingRight);
  
  // STEP 1: Collect all "fixed" nodes to the left of the swap area
  const fixedNodesLeft = [];
  for (let i = 0; i < leftIndex; i++) {
    const chainNodes = buildChainNodesWithMilestones(sortedChains[i], tasks, positions, nodeDimensions);
    fixedNodesLeft.push(...chainNodes);
  }
  
  // STEP 2: Position the chain moving LEFT using tetris logic (move as far left as possible)
  const targetXForLeft = fixedNodesLeft.length > 0
    ? calculateTetrisPositionLeft(nodesMovingLeft, fixedNodesLeft, HORIZONTAL_GAP)
    : PADDING;
  
  const shiftLeft = boundsMovingLeft.minX - targetXForLeft;
  
  // Apply shift to chain moving left
  nodesMovingLeft.forEach(node => {
    newPositions[node.id] = {
      x: Math.round(node.x - shiftLeft),
      y: node.y,
    };
    // Update node position for subsequent calculations
    node.x -= shiftLeft;
    node.right -= shiftLeft;
  });
  
  // STEP 3: Position the chain moving RIGHT using tetris logic
  // This chain needs to be positioned to the RIGHT of the chain that moved left
  // Use calculateTetrisPositionRight to find the minimum X that avoids overlap
  const targetXForRight = calculateTetrisPositionRight(nodesMovingRight, nodesMovingLeft, HORIZONTAL_GAP);
  const shiftRight = boundsMovingRight.minX - targetXForRight;
  
  // Apply shift to chain moving right (shiftRight will be negative, moving it right)
  nodesMovingRight.forEach(node => {
    newPositions[node.id] = {
      x: Math.round(node.x - shiftRight),
      y: node.y,
    };
    // Update node position for subsequent calculations
    node.x -= shiftRight;
    node.right -= shiftRight;
  });
  
  // STEP 4: Handle chains AFTER the swap area
  // These chains need to:
  // 1. Move RIGHT if the swapped chains now overlap them (avoid collision)
  // 2. Move LEFT if there's now a gap (compact)
  // We use calculateTetrisPositionRight which finds the minimum valid X position
  // that avoids overlap - this handles both cases correctly
  if (rightIndex + 1 < sortedChains.length) {
    // Combine both swapped chains as the "fixed" reference for chains after
    const fixedNodes = [...nodesMovingLeft, ...nodesMovingRight];
    
    // Process each chain after the swap, one by one
    for (let i = rightIndex + 1; i < sortedChains.length; i++) {
      const afterChain = sortedChains[i];
      const afterNodes = buildChainNodesWithMilestones(afterChain, tasks, positions, nodeDimensions);
      const afterBounds = getChainBounds(afterNodes);
      
      if (afterBounds && afterNodes.length > 0) {
        // Calculate where this chain needs to be to avoid overlap with fixed nodes
        // This will push it right if there's overlap, or allow it to stay/move left if there's space
        const targetXForAfter = calculateTetrisPositionRight(afterNodes, fixedNodes, HORIZONTAL_GAP);
        const afterShift = afterBounds.minX - targetXForAfter;
        
        // Apply shift to this chain (negative shift = move right, positive = move left)
        afterNodes.forEach(node => {
          newPositions[node.id] = {
            x: Math.round(node.x - afterShift),
            y: node.y,
          };
          // Update node position for subsequent calculations
          node.x -= afterShift;
          node.right -= afterShift;
        });
        
        // Add this chain's nodes to fixedNodes for the next iteration
        fixedNodes.push(...afterNodes);
      }
    }
  }
  
  return newPositions;
}
